import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/lib/store";
import ProductCard from "@/components/ProductCard";
import { ShieldCheck, Truck, RotateCcw, Star, ChevronRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function Product() {
  const { slug } = useParams();
  const { addItem, setOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [selectedMl, setSelectedMl] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/products/${slug}`).then(({ data }) => {
      setProduct(data);
      setSelectedMl(data.sizes[data.sizes.length - 1].ml);
      api.get(`/reviews/${data.id}`).then(({ data }) => setReviews(data));
    });
    api.get(`/products/${slug}/similar`).then(({ data }) => setSimilar(data));
  }, [slug]);

  if (!product) {
    return <div className="p-24 text-center text-muted-foreground">Cargando…</div>;
  }

  const selectedSize = product.sizes.find((s) => s.ml === selectedMl);
  const addToBag = () => {
    addItem(product, selectedMl, 1);
    toast.success("Añadido a tu bolsa", { description: `${product.name} — ${selectedMl}ml` });
  };

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-8 md:py-12">
      {/* Breadcrumb */}
      <nav className="font-mono-caps text-xs text-muted-foreground mb-8" data-testid="pdp-breadcrumb">
        <Link to="/" className="hover:text-foreground">Inicio</Link> · <Link to="/shop" className="hover:text-foreground">Boutique</Link> · <Link to={`/shop?brand=${product.brand}`} className="hover:text-foreground">{product.brand}</Link> · <span className="text-foreground">{product.name}</span>
      </nav>

      <div className="grid md:grid-cols-2 gap-8 md:gap-16">
        {/* Gallery */}
        <div className="md:sticky md:top-32 self-start space-y-4" data-testid="pdp-gallery">
          <div className="aspect-square bg-white border border-border overflow-hidden">
            <img src={product.images[activeImg]} alt={product.name} className="w-full h-full object-contain p-6" />
          </div>
          <div className="flex gap-3">
            {product.images.map((img, i) => (
              <button key={i} onClick={() => setActiveImg(i)} className={`w-20 h-20 overflow-hidden border ${i === activeImg ? "border-navy" : "border-transparent"}`} data-testid={`pdp-thumb-${i}`}>
                <img src={img} alt="" className="w-full h-full object-contain bg-white p-1" />
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <div className="space-y-8">
          <div>
            <div className="flex flex-wrap gap-2 mb-4">
              {product.badges.map((b) => (
                <span key={b} className="font-mono-caps text-[10px] border border-navy px-2 py-1">{b}</span>
              ))}
            </div>
            <Link to={`/shop?brand=${product.brand}`} className="font-mono-caps text-muted-foreground editorial-link">{product.brand}</Link>
            <h1 className="font-serif text-4xl md:text-5xl leading-tight mt-2" data-testid="pdp-name">{product.name}</h1>

            <div className="flex items-center gap-3 mt-4 text-sm">
              <div className="flex items-center gap-1 text-royal">
                {[1,2,3,4,5].map(i => <Star key={i} strokeWidth={1} fill={i <= Math.round(product.rating) ? "currentColor" : "none"} className="h-4 w-4" />)}
              </div>
              <span className="text-muted-foreground">{product.rating.toFixed(1)} · {product.reviews_count} opiniones verificadas</span>
            </div>
          </div>

          {/* Sizes */}
          <div>
            <div className="font-mono-caps text-xs mb-3">Tamaño · {product.concentration}</div>
            <div className="flex flex-wrap gap-3" data-testid="pdp-sizes">
              {product.sizes.map((s) => (
                <button
                  key={s.ml}
                  onClick={() => setSelectedMl(s.ml)}
                  className={`border px-6 py-4 font-mono-caps text-xs transition-colors ${selectedMl === s.ml ? "border-navy bg-navy text-cream" : "border-border hover:border-navy"}`}
                  data-testid={`pdp-size-${s.ml}`}
                >
                  {s.ml}ml <span className="ml-3 opacity-70">${s.price.toFixed(2)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Price + CTA */}
          <div className="border-y border-border py-6 space-y-4">
            <div className="flex items-baseline justify-between">
              <div className="flex items-baseline gap-4">
                {selectedSize?.compare_at_price && selectedSize.compare_at_price > selectedSize.price ? (
                  <>
                    <div
                      className="font-serif text-4xl text-[#8B0000]"
                      data-testid="pdp-price"
                    >
                      ${selectedSize.price.toFixed(2)}
                    </div>
                    <div
                      className="font-serif text-2xl text-muted-foreground line-through"
                      data-testid="pdp-compare-price"
                    >
                      ${selectedSize.compare_at_price.toFixed(2)}
                    </div>
                    <div
                      className="font-mono-caps text-xs bg-[#8B0000] text-cream px-2 py-1"
                      data-testid="pdp-discount-badge"
                    >
                      −{Math.round(((selectedSize.compare_at_price - selectedSize.price) / selectedSize.compare_at_price) * 100)}%
                    </div>
                  </>
                ) : (
                  <div className="font-serif text-4xl" data-testid="pdp-price">${selectedSize?.price.toFixed(2)}</div>
                )}
              </div>
              <div className="font-mono-caps text-xs text-muted-foreground">
                {selectedSize?.stock > 5 ? "En stock" : selectedSize?.stock > 0 ? `Últimas ${selectedSize.stock} unidades` : "Agotado"}
              </div>
            </div>
            <button
              onClick={addToBag}
              disabled={selectedSize?.stock === 0}
              data-testid="pdp-add-to-cart"
              className="w-full bg-navy text-cream py-4 font-mono-caps hover:bg-cream hover:text-navy transition-colors disabled:opacity-30"
            >
              Añadir a la bolsa
            </button>
            <div className="grid grid-cols-3 gap-4 pt-2">
              <div className="flex flex-col items-center text-center gap-2 text-xs">
                <ShieldCheck strokeWidth={1} className="h-6 w-6 text-navy" />
                <span>Original 100%</span>
              </div>
              <div className="flex flex-col items-center text-center gap-2 text-xs">
                <Truck strokeWidth={1} className="h-6 w-6 text-navy" />
                <span>Envío 24-48h</span>
              </div>
              <div className="flex flex-col items-center text-center gap-2 text-xs">
                <RotateCcw strokeWidth={1} className="h-6 w-6 text-navy" />
                <span>Devolución 30 días</span>
              </div>
            </div>
          </div>

          {/* Description */}
          <p className="text-base leading-relaxed text-foreground/80" data-testid="pdp-description">{product.description}</p>

          {/* Olfactive pyramid — only render if we have notes */}
          {(product.top_notes.length + product.heart_notes.length + product.base_notes.length) > 0 && (
          <div data-testid="pdp-pyramid">
            <div className="font-mono-caps text-xs mb-6">Pirámide olfativa</div>
            <div className="space-y-6">
              {[{ label: "Salida", notes: product.top_notes }, { label: "Corazón", notes: product.heart_notes }, { label: "Fondo", notes: product.base_notes }].map((row) => (
                <div key={row.label} className="grid grid-cols-4 gap-6 items-start border-b border-border pb-6">
                  <div className="font-serif text-xl italic text-royal">{row.label}</div>
                  <div className="col-span-3 flex flex-wrap items-center gap-x-2 gap-y-1">
                    {row.notes.map((n, idx) => (
                      <span key={`${row.label}-${n}`} className="flex items-center text-sm">
                        {idx > 0 && <span className="text-muted-foreground mx-2">·</span>}
                        {n}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* History & Origin */}
          {(product.history || product.origin) && (
            <section className="border-t border-border pt-6 space-y-4" data-testid="pdp-history">
              <div className="font-mono-caps text-xs text-royal">Historia y origen</div>
              {product.history && (
                <p className="text-base leading-relaxed text-foreground/80 font-serif italic">"{product.history}"</p>
              )}
              {product.origin && (
                <div className="flex items-baseline gap-2 text-sm">
                  <span className="text-muted-foreground text-xs font-mono-caps">Origen</span>
                  <span>{product.origin}</span>
                </div>
              )}
            </section>
          )}

          {/* Usage recommendation */}
          {(product.usage_time || product.usage_climate || product.usage_occasion) && (
            <section className="border-t border-border pt-6" data-testid="pdp-usage">
              <div className="font-mono-caps text-xs text-royal mb-4">Recomendación de uso</div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                {product.usage_time && (
                  <div className="border border-border p-4">
                    <div className="text-muted-foreground text-[10px] font-mono-caps">Momento</div>
                    <div className="font-serif text-lg mt-1 capitalize">{product.usage_time}</div>
                  </div>
                )}
                {product.usage_climate && (
                  <div className="border border-border p-4">
                    <div className="text-muted-foreground text-[10px] font-mono-caps">Clima</div>
                    <div className="font-serif text-lg mt-1 capitalize">{product.usage_climate}</div>
                  </div>
                )}
                {product.season && product.season.length > 0 && (
                  <div className="border border-border p-4">
                    <div className="text-muted-foreground text-[10px] font-mono-caps">Estación</div>
                    <div className="font-serif text-lg mt-1 capitalize">{product.season.join(" · ")}</div>
                  </div>
                )}
              </div>
              {product.usage_occasion && (
                <p className="mt-4 text-sm leading-relaxed text-foreground/80">{product.usage_occasion}</p>
              )}
            </section>
          )}

          <Tabs defaultValue="details" className="pt-4">
            <TabsList className="rounded-none border-b border-border w-full justify-start bg-transparent p-0 h-auto">
              <TabsTrigger value="details" className="rounded-none border-b-2 border-transparent data-[state=active]:border-navy data-[state=active]:shadow-none data-[state=active]:bg-transparent font-mono-caps text-xs pb-3">Detalles</TabsTrigger>
              <TabsTrigger value="authenticity" className="rounded-none border-b-2 border-transparent data-[state=active]:border-navy data-[state=active]:shadow-none data-[state=active]:bg-transparent font-mono-caps text-xs pb-3">Autenticidad</TabsTrigger>
              <TabsTrigger value="reviews" className="rounded-none border-b-2 border-transparent data-[state=active]:border-navy data-[state=active]:shadow-none data-[state=active]:bg-transparent font-mono-caps text-xs pb-3">Opiniones</TabsTrigger>
            </TabsList>
            <TabsContent value="details" className="pt-6 text-sm space-y-3 leading-relaxed">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-muted-foreground text-xs">Longevidad</div><div>{product.longevity}</div></div>
                <div><div className="text-muted-foreground text-xs">Proyección</div><div>{product.projection}</div></div>
                <div><div className="text-muted-foreground text-xs">Estación</div><div className="capitalize">{product.season.join(", ")}</div></div>
                <div><div className="text-muted-foreground text-xs">Ocasión</div><div className="capitalize">{product.occasion.join(", ")}</div></div>
                {product.similar_to && (
                  <div className="col-span-2"><div className="text-muted-foreground text-xs">Similar a</div><div className="italic">{product.similar_to}</div></div>
                )}
              </div>
            </TabsContent>
            <TabsContent value="authenticity" className="pt-6 text-sm leading-relaxed space-y-3">
              <p><strong>Lote:</strong> {product.lot_number}</p>
              <p>Todos nuestros perfumes son 100% originales. No vendemos imitaciones ni testers sin indicación. Trabajamos con distribuidores oficiales y verificamos cada lote. Garantía de autenticidad o te devolvemos tu dinero.</p>
              <Link to="/autenticidad" className="editorial-link font-mono-caps text-xs inline-flex items-center gap-2">Cómo lo verificamos <ChevronRight strokeWidth={1.25} className="h-3 w-3" /></Link>
            </TabsContent>
            <TabsContent value="reviews" className="pt-6 space-y-6">
              {reviews.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aún no hay opiniones. Sé el primero.</p>
              ) : (
                reviews.map((r) => (
                  <div key={r.id} className="border-b border-border pb-6" data-testid={`review-${r.id}`}>
                    <div className="flex items-center gap-2 text-xs text-royal mb-2">
                      {[1,2,3,4,5].map(i => <Star key={i} strokeWidth={1} fill={i <= r.rating ? "currentColor" : "none"} className="h-3 w-3" />)}
                      <span className="text-muted-foreground">· Verificado</span>
                    </div>
                    <div className="font-serif text-lg">{r.title}</div>
                    <p className="text-sm mt-2 leading-relaxed">{r.body}</p>
                    <div className="font-mono-caps text-xs text-muted-foreground mt-3">— {r.author_name}</div>
                  </div>
                ))
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Similar */}
      {similar.length > 0 && (
        <section className="mt-24 md:mt-32" data-testid="pdp-similar">
          <h2 className="font-serif text-3xl md:text-4xl mb-10">Fragancias afines</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {similar.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
